import {
  OPTION_RISK_LEVELS,
  OPTION_THRESHOLDS,
} from "./NinaOptionsScannerSchema";
import { calculateOptionsScores } from "./NinaOptionsScore";
import { validateOptionSnapshot } from "./NinaOptionsScannerValidation";

const STATE_GROUP = {
  "Critical risk": 0,
  "Ready to review": 1,
  Building: 2,
  "Insufficient evidence": 3,
  "Risk blocked": 4,
  Monitoring: 5,
  Weakening: 5,
};

const riskRank = (risk) => OPTION_RISK_LEVELS.indexOf(risk);
const evidenceRank = {
  unavailable: 0,
  hypothesis: 1,
  "static-demo": 2,
  simulated: 3,
  "system-derived": 4,
};
const factorScore = (result, key) =>
  result.signals[key].status === "unavailable"
    ? -1
    : result.signals[key].score;

export function evaluateOptionSnapshot(snapshot, { now = Date.now() } = {}) {
  const validation = validateOptionSnapshot(snapshot, { now });
  if (!validation.valid) return { valid: false, error: validation.error, result: null };
  const value = validation.value;
  const scores = calculateOptionsScores(value, now);
  const threshold = OPTION_THRESHOLDS[value.submode];
  const spreadPercent = (value.spread / value.ask) * 100;
  const ageSeconds = Math.max(0, (now - Date.parse(value.updatedAt)) / 1000);
  const blockReasons = [];
  scores.missingCritical.forEach((label) =>
    blockReasons.push(`Critical evidence is unavailable: ${label}.`)
  );
  if (spreadPercent > threshold.maximumSpreadPercent) {
    blockReasons.push(
      `Contract spread ${spreadPercent.toFixed(2)}% exceeds the ${threshold.maximumSpreadPercent}% ${value.submode} limit.`
    );
  }
  if (value.volume < threshold.minimumVolume) {
    blockReasons.push(`Volume is below the ${threshold.minimumVolume} minimum.`);
  }
  if (value.openInterest < threshold.minimumOpenInterest) {
    blockReasons.push(`Open interest is below the ${threshold.minimumOpenInterest} minimum.`);
  }
  if (ageSeconds > threshold.maximumAgeSeconds) {
    blockReasons.push("Contract pricing is stale for this options mode.");
  }
  if (!Number.isFinite(value.maxLoss) || value.maxLoss <= 0) {
    blockReasons.push("Maximum loss cannot be calculated.");
  }
  if (value.dte < threshold.dteMin || value.dte > threshold.dteMax) {
    blockReasons.push("Expiration does not match the selected options mode.");
  }
  if (value.impliedVolatility >= threshold.criticalIvPercent) {
    blockReasons.push("Implied-volatility risk is critical.");
  }
  if (value.underlyingRisk === "Critical") {
    blockReasons.push("Underlying risk is Critical.");
  }
  const criticalRisk =
    value.riskLevel === "Critical" || value.underlyingRisk === "Critical";
  let state;
  if (criticalRisk) state = "Risk blocked";
  else if (blockReasons.length) {
    state = scores.missingCritical.length === blockReasons.length
      ? "Insufficient evidence"
      : "Risk blocked";
  } else if (
    scores.suitability >= threshold.readySuitability &&
    scores.confidence >= threshold.readyConfidence
  ) state = "Ready to review";
  else if (scores.suitability >= 55) state = "Building";
  else state = "Monitoring";
  const priorityGroup = criticalRisk ? 0 : STATE_GROUP[state];
  const stableKey = [
    value.symbol,
    value.contractType,
    value.expiration,
    value.strike.toFixed(4),
    value.submode,
    value.setup,
    value.alertType,
    value.contractId,
  ].join("|");
  return {
    valid: true,
    error: "",
    result: {
      ...value,
      scores,
      spreadPercent,
      ageSeconds,
      blockReasons,
      state,
      criticalRisk,
      priorityGroup,
      stableKey,
      occurrenceCount: 1,
      latestSeenAt: value.updatedAt,
    },
  };
}

export function evaluateOptionsData(snapshots, options = {}) {
  const errors = [];
  const byKey = new Map();
  let validCount = 0;
  snapshots.forEach((snapshot) => {
    const evaluation = evaluateOptionSnapshot(snapshot, options);
    if (!evaluation.valid) {
      errors.push(evaluation.error);
      return;
    }
    validCount += 1;
    const result = evaluation.result;
    const current = byKey.get(result.stableKey);
    if (!current) byKey.set(result.stableKey, result);
    else {
      byKey.set(result.stableKey, {
        ...(Date.parse(result.updatedAt) >= Date.parse(current.updatedAt) ? result : current),
        occurrenceCount: current.occurrenceCount + 1,
        latestSeenAt:
          Date.parse(result.updatedAt) >= Date.parse(current.latestSeenAt)
            ? result.updatedAt
            : current.latestSeenAt,
      });
    }
  });
  const results = [...byKey.values()].sort(compareOptionResults);
  return {
    results,
    errors,
    validCount,
    invalidCount: errors.length,
  };
}

export function compareOptionResults(a, b) {
  return (
    a.priorityGroup - b.priorityGroup ||
    riskRank(a.riskLevel) - riskRank(b.riskLevel) ||
    factorScore(b, "liquidity") - factorScore(a, "liquidity") ||
    factorScore(b, "spreadQuality") - factorScore(a, "spreadQuality") ||
    b.scores.completeness - a.scores.completeness ||
    evidenceRank[b.evidenceStatus] - evidenceRank[a.evidenceStatus] ||
    b.scores.confidence - a.scores.confidence ||
    b.scores.smartFlow - a.scores.smartFlow ||
    a.stableKey.localeCompare(b.stableKey)
  );
}

const materialChange = (previous, next) =>
  previous.state !== next.state ||
  previous.riskLevel !== next.riskLevel ||
  Math.abs(previous.scores.suitability - next.scores.suitability) >= 8 ||
  Math.abs(previous.scores.confidence - next.scores.confidence) >= 8 ||
  Math.abs(previous.spreadPercent - next.spreadPercent) >= 5 ||
  previous.blockReasons.join("|") !== next.blockReasons.join("|") ||
  next.scores.completeness > previous.scores.completeness;

export function shouldRealertOption(previous, next, { cooldownMs = 60000 } = {}) {
  if (!previous) return true;
  const elapsed = Date.parse(next.updatedAt) - Date.parse(previous.updatedAt);
  if (!materialChange(previous, next)) return false;
  return elapsed >= 0 && (elapsed >= cooldownMs || next.criticalRisk);
}
